import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Aviso } from '@/components/ui/aviso';
import { EstadoError } from '@/components/ui/estado-vacio';
import { cn } from '@/lib/utils';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { CategoryMultiSelect } from '@/features/pricing-rules/components/category-multi-select';
import { BrandMultiSelect } from '@/features/pricing-rules/components/brand-multi-select';
import { BuscadorDeExcepciones } from '@/features/pricing-rules/components/buscador-de-excepciones';
import { SelectorDeProductos } from '@/features/pricing-rules/components/selector-de-productos';
import { formatoPrecio } from '@/features/pricing-rules/components/barra-de-rangos';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { urlDeImagen } from '@/lib/utils/imagen-de-producto';
import {
  ESPERA_AL_TIPEAR_MS,
  usePedidoConEspera,
} from '@/features/pricing-rules/lib/use-pedido-con-espera';
import { strategyApplicationsApi } from '../../services/strategy-applications-api';
import type { ProductoElegible } from '../../types/strategy-applications.types';
import { grupoParaContar, type BorradorDeEstrategia } from './borrador';
import { fuenteDelAlcance, TODO_EL_CATALOGO } from './fuente-del-alcance';

/** D4 (27-sep): se abrió desde Análisis por categoría, cuya tarjeta cuenta sólo los que tienen competencia. */
export interface DesdeAnalisis {
  enLaTarjeta: number;
}

/**
 * Paso 1 · A quién alcanza (maqueta `C-Nueva1-Alcance`). Sólo lo que el
 * producto ES: su categoría, su marca, o productos puntuales. El estado que
 * cambia (margen, rotación, stock, precio) NO se ofrece: vive en Reglas. El
 * backend lo sigue aceptando por compatibilidad; la pantalla nueva no lo manda.
 *
 * Los que entran salen de `POST /api/strategy-applications/productos` (plan 1,
 * T1): la misma resolución del alcance que usa la corrida. Se piden con el
 * nombre fijo de `grupoParaContar` (27-sep, B5): el nombre no cambia a quién
 * alcanza, y si entrara en la clave cada tecla volvería a pedir.
 */
export function Paso1Alcance({
  borrador: b,
  onChange,
  desdeAnalisis = null,
}: {
  borrador: BorradorDeEstrategia;
  onChange: (cambios: Partial<BorradorDeEstrategia>) => void;
  /** D4: la tarjeta de Análisis contaba menos (sólo los con competencia) que toda la categoría. */
  desdeAnalisis?: DesdeAnalisis | null;
}) {
  const grupo = grupoParaContar(b);
  // Lo que se lista para elegir excepciones: el alcance antes de sacar a nadie.
  const alcanceSinExcepciones = grupoParaContar({ ...b, excluidos: [] });
  const losQueEntran = usePedidoConEspera(grupo ? JSON.stringify(grupo) : null, () =>
    strategyApplicationsApi.productosDelAlcance(grupo!, 1, 20)
  );

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="nombre-estrategia">Nombre</Label>
        <Input
          id="nombre-estrategia"
          value={b.nombre}
          onChange={(e) => onChange({ nombre: e.target.value })}
          placeholder="Por ejemplo: Sony al más bajo"
        />
      </div>

      <div role="radiogroup" aria-label="A quién alcanza" className="flex gap-2">
        {(
          [
            ['CATEGORIA_Y_MARCA', 'Por categoría y marca'],
            ['PRODUCTOS', 'Productos puntuales'],
          ] as const
        ).map(([forma, texto]) => (
          <label
            key={forma}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm',
              b.forma === forma && 'border-primary bg-primary/5'
            )}
          >
            <input
              type="radio"
              name="forma-del-alcance"
              aria-label={texto}
              checked={b.forma === forma}
              onChange={() => onChange({ forma })}
            />
            {texto}
          </label>
        ))}
      </div>

      {b.forma === 'CATEGORIA_Y_MARCA' ? (
        <div className="space-y-4">
          <div role="group" aria-label="Categoría" className="space-y-1.5">
            <div className="text-sm font-medium">Categoría</div>
            <CategoryMultiSelect
              selected={b.categorias}
              onChange={(categorias) => onChange({ categorias })}
            />
          </div>
          <div role="group" aria-label="Marca" className="space-y-1.5">
            <div className="text-sm font-medium">Marca</div>
            <BrandMultiSelect selected={b.marcas} onChange={(marcas) => onChange({ marcas })} />
            <p className="text-xs text-muted-foreground">
              Muchos productos no tienen la marca cargada: los que no la tienen no entran por marca.
            </p>
          </div>
          <div role="group" aria-label="Menos estos" className="space-y-1.5">
            <div className="text-sm font-medium">Menos estos</div>
            <BuscadorDeExcepciones
              excluidos={b.excluidos}
              onChange={(excluidos) => onChange({ excluidos })}
              deDonde="la estrategia"
              {...(alcanceSinExcepciones && { fuente: fuenteDelAlcance(alcanceSinExcepciones) })}
            />
          </div>
        </div>
      ) : (
        <ElegirProductos skus={b.skus} onChange={(skus) => onChange({ skus })} />
      )}

      <p
        data-testid="aclaracion-membresia"
        className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground"
      >
        Entrar en la estrategia no quiere decir que se le mueva el precio: eso lo deciden el método
        (paso 2), las reglas y el permiso (paso 3).
      </p>

      <section aria-label="Los que entran" className="space-y-2">
        <div className="text-sm font-medium">Los que entran</div>
        {!grupo ? (
          <p className="text-sm text-muted-foreground">
            {b.forma === 'PRODUCTOS'
              ? 'Elegí al menos un producto.'
              : 'Elegí al menos una categoría o una marca.'}
          </p>
        ) : losQueEntran.estado === 'ERROR' ? (
          <EstadoError onReintentar={losQueEntran.reintentar}>
            {mensajeDeError(losQueEntran.error, 'No se pudo calcular a quién alcanza.')}
          </EstadoError>
        ) : losQueEntran.estado !== 'LISTO' ? (
          <p className="text-sm text-muted-foreground">Contando…</p>
        ) : (
          <>
            <p data-testid="cuantos-entran" className="text-sm">
              Alcanza a <strong>{formatoCantidad(losQueEntran.data.total)}</strong>{' '}
              {losQueEntran.data.total === 1 ? 'producto' : 'productos'}
              {losQueEntran.data.productos.length < losQueEntran.data.total &&
                ` (se ven los primeros ${losQueEntran.data.productos.length})`}
              .
            </p>
            {/* D4 (27-sep): la tarjeta de Análisis cuenta los que tienen competencia;
                la estrategia alcanza a la categoría entera, también a los que entren después. */}
            {desdeAnalisis && b.forma === 'CATEGORIA_Y_MARCA' && (
              <Aviso tono="info" data-testid="aviso-categoria">
                {`En Análisis la tarjeta contaba ${formatoCantidad(desdeAnalisis.enLaTarjeta)} (los que tienen competencia). La estrategia alcanza a toda la categoría: hoy ${formatoCantidad(losQueEntran.data.total)} ${losQueEntran.data.total === 1 ? 'producto' : 'productos'}, y también a los que entren después.`}
              </Aviso>
            )}
            <ul className="max-h-56 divide-y overflow-y-auto rounded-md border text-sm">
              {losQueEntran.data.productos.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-3 py-1.5">
                  {p.imageUrl ? (
                    <img
                      src={urlDeImagen(p.imageUrl) ?? undefined}
                      alt=""
                      className="h-8 w-8 rounded object-cover"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded bg-muted" aria-hidden />
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    {p.name} <span className="text-xs text-muted-foreground">{p.sku}</span>
                  </span>
                  <span className="text-xs tabular-nums">{formatoPrecio(p.currentPrice)}</span>
                  <span className="w-40 truncate text-right text-xs text-muted-foreground">
                    {/* Nº 16: vacío = «ninguna otra»; «—» es «no sé», y esto sí se sabe. */}
                    {p.yaLaTiene ? `ya la tiene «${p.yaLaTiene.applicationName}»` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}

/** Elegir productos a mano: la búsqueda va al servidor (el endpoint devuelve 100 como mucho). */
function ElegirProductos({
  skus,
  onChange,
}: {
  skus: string[];
  onChange: (skus: string[]) => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [enLista, setEnLista] = useState(false);
  // Escaneo previo Nº 14: la espera al tipear y el descarte de respuestas viejas
  // son los de todas las pantallas (`usePedidoConEspera`); sin texto, pide en el acto.
  const pedido = usePedidoConEspera<{ productos: ProductoElegible[]; total: number }>(
    busqueda,
    () => strategyApplicationsApi.productos(busqueda),
    busqueda.trim() ? ESPERA_AL_TIPEAR_MS : 0
  );
  const lista = pedido.estado === 'LISTO' ? pedido.data.productos : [];
  const total = pedido.estado === 'LISTO' ? pedido.data.total : 0;

  const alternar = (sku: string) =>
    onChange(skus.includes(sku) ? skus.filter((s) => s !== sku) : [...skus, sku]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Productos</span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {skus.length} elegido{skus.length === 1 ? '' : 's'}
          </span>
          <Button type="button" size="sm" variant="outline" onClick={() => setEnLista(true)}>
            Elegir de la lista…
          </Button>
        </div>
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre o código"
          className="pl-8"
        />
      </div>
      <div className="max-h-56 overflow-y-auto rounded-md border">
        {pedido.estado === 'ERROR' ? (
          <div className="p-2">
            <EstadoError onReintentar={pedido.reintentar}>
              {mensajeDeError(pedido.error, 'No se pudo traer la lista de productos.')}
            </EstadoError>
          </div>
        ) : pedido.estado !== 'LISTO' ? (
          <p className="p-3 text-sm text-muted-foreground">Buscando…</p>
        ) : lista.length === 0 ? (
          <p className="p-4 text-center text-sm text-muted-foreground">
            {busqueda.trim()
              ? 'Ningún producto coincide con la búsqueda.'
              : 'Esta empresa todavía no tiene productos cargados.'}
          </p>
        ) : (
          lista.map((p) => (
            <label
              key={p.id}
              className="flex cursor-pointer items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0 hover:bg-muted/50"
            >
              <Checkbox checked={skus.includes(p.sku)} onCheckedChange={() => alternar(p.sku)} />
              <span className="flex-1">
                {p.name}
                <span className="ml-2 text-xs text-muted-foreground">{p.sku}</span>
              </span>
            </label>
          ))
        )}
      </div>
      {pedido.estado === 'LISTO' && total > lista.length && (
        <p className="text-xs text-muted-foreground">
          Mostrando {formatoCantidad(lista.length)} de {formatoCantidad(total)}. Buscá por nombre o
          código para encontrar el resto.
        </p>
      )}
      <SelectorDeProductos
        abierto={enLista}
        titulo="Elegir los productos"
        descripcion="Todo el catálogo. La estrategia alcanza sólo a los que marques."
        fuente={fuenteDelAlcance(TODO_EL_CATALOGO)}
        elegidos={skus}
        textoConfirmar={(n) => (n === 1 ? 'Usar 1 producto' : `Usar ${n} productos`)}
        onConfirmar={(elegidos) => {
          onChange(elegidos);
          setEnLista(false);
        }}
        onCerrar={() => setEnLista(false)}
      />
    </div>
  );
}
